<template>
    <!-- A new item on a meeting board: the header's "Agenda Item" quick add (any meeting you can open), and an
         Issue from a scorecard metric (preset filled in). With meeting set (a running meeting) it goes on that one and
         only the column is picked. Same look and fields as a new item on the meeting board (AddEditMeetingItem.vue). -->
    <div class="error-msg-wrap">
        <div class="form-wrapper slide-up crm-form-wrapper crm-form-new">
            <div class="form-close">
                <button type="button" class="form-close-x" title="Close" @click="$emit('close')">&times;</button>
            </div>
            <div class="qc-item-wrap crm-item-wrap crm-item-wrap-bare">
                <div>
                    <h3 class="quick-add-heading">{{ heading }}</h3>
                    <p v-if="!loaded" class="quick-add-note">Loading your meetings&hellip;</p>
                    <p v-else-if="!meetings.length" class="quick-add-note">You don't have any meetings to add to yet.</p>
                    <template v-else>
                        <div class="quick-add-row">
                            <div v-if="!fixedMeeting">
                                <div class="crm-item-label" style="margin-top:0">Meeting</div>
                                <select v-model="meetingID" @change="meetingChange()">
                                    <optgroup v-for="g in meetingGroups" :key="g.label" :label="g.label">
                                        <option v-for="m in g.meetings" :key="m.ID" :value="m.ID">{{ m.Name }}</option>
                                    </optgroup>
                                </select>
                            </div>
                            <div>
                                <div class="crm-item-label" style="margin-top:0">Column</div>
                                <select v-model="column">
                                    <option v-for="c in columns" :key="c.name" :value="c.name">{{ c.name }}</option>
                                </select>
                            </div>
                        </div>

                        <div class="crm-item-label">Name</div>
                        <input type="text" style="margin:0" v-model="form.Name" ref="nameInput" @keyup.enter="submit()" />

                        <div class="quick-add-row">
                            <div>
                                <div class="crm-item-label">Priority</div>
                                <select v-model="form.Priority">
                                    <option :value="null">No Priority</option>
                                    <option value="high">High</option>
                                    <option value="med">Medium</option>
                                    <option value="low">Low</option>
                                </select>
                            </div>
                            <div>
                                <div class="crm-item-label">Assigned To</div>
                                <select v-model="form.AssigneeEmployeeID">
                                    <option :value="null">Unassigned</option>
                                    <option v-for="emp in assigneeOptions" :key="emp.ID" :value="emp.ID">{{ emp.Name }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="quick-add-row">
                            <div>
                                <div class="crm-item-label">Due Date</div>
                                <input type="date" v-model="form.DueDate" :class="dueDateNudge ? 'due-date-missing' : ''" />
                                <div v-if="dueDateNudge" class="due-date-nudge">No due date yet. We recommend setting one.</div>
                            </div>
                            <div></div>
                        </div>

                        <!-- Same switch as a new item on the meeting board -->
                        <label class="item-private">
                            <input type="checkbox" v-model="form.Private" />
                            <span class="item-private-track"><span class="item-private-knob"></span></span>
                            <span class="item-private-text">
                                <span class="item-private-title">Only visible to me</span>
                                <span class="item-hint">Other attendees won't see this card on the board or in the meeting history.</span>
                            </span>
                        </label>
                        <div v-if="form.Private && form.AssigneeEmployeeID && form.AssigneeEmployeeID != $store.state.user.employeeID" class="item-private-warn">
                            {{ assigneeName || 'The assignee' }} won't see this card while it's only visible to you.
                        </div>

                        <div class="crm-item-label">Description</div>
                        <div class="board-tip-tap">
                            <tip-tap v-model="form.Description" :hide-h3="true" :hide-h4="true" :hide-table="true" />
                        </div>

                        <input style="margin-top:20px" :class="saving ? 'disable-button' : ''" type="submit" :value="dueDateNudge ? 'Submit Without Due Date' : 'Submit'" @click="submit()" />
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import TipTap from '@/components/TipTap.vue';

export default defineComponent({
    name: 'QuickAddAgendaItem',
    props: {
        // A running meeting (its ColumnsJSON current): the item goes on it, and only the column is picked
        fixedMeeting: { type: Object, default: null },
        // The item already started: Name, Description, AssigneeEmployeeID… (e.g. a metric's Issue, scoreboard.ts)
        preset: { type: Object, default: null },
        // Start on one of this team's meetings (the last one used, if it was one)
        team: { type: String, default: '' },
        // Start in the first column whose name contains this (e.g. "Issues"), else the first column
        preferColumn: { type: String, default: '' },
        heading: { type: String, default: 'Add Agenda Item' }
    },
    emits: [ 'close', 'added' ],
    components: {
        TipTap
    },
    data() {
        return {
            loaded: false,
            groups: [] as any[], // meeting groups (teams) you can see
            meetings: [] as any[], // upcoming and recurring meetings, each with its Group
            employees: [] as any[],
            teamMembers: {} as any, // team name: employee IDs who can open its meetings (attendee-options)
            meetingID: null as any,
            column: '',
            form: { Name: '', Priority: null as any, AssigneeEmployeeID: null as any, DueDate: '', Description: '', Private: false },
            saving: false,
            dueDateAsked: false // Submit was pressed with no due date (the next Submit goes through)
        }
    },
    async created() {
        // Opened from somewhere with the item already started (a metric's Issue, a personal goal's Bring to Meeting)
        if(this.preset) {
            Object.assign(this.form, this.preset);
        }
        if(this.$store.state.quickAddPreset) {
            Object.assign(this.form, this.$store.state.quickAddPreset);
            this.$store.state.quickAddPreset = null;
        }
        try {
            if(this.fixedMeeting) {
                const employees = await axios.get(`${this.$store.state.apiUrl}/api/employees/limitedinfo`);
                this.employees = employees.data.map((e: any) => ({ ...e, ID: Number(e.ID) }));
                this.meetings = [ { ...this.fixedMeeting, Group: this.fixedMeeting.Team || null } ];
            } else {
                const [ groups, mine, employees ] = await Promise.all([
                    axios.get(`${this.$store.state.apiUrl}/api/meetings/groups`),
                    axios.get(`${this.$store.state.apiUrl}/api/meetings`),
                    axios.get(`${this.$store.state.apiUrl}/api/employees/limitedinfo`)
                ]);
                this.groups = groups.data;
                this.employees = employees.data.map((e: any) => ({ ...e, ID: Number(e.ID) }));
                const byGroup = await Promise.all(this.groups.map((g: any) =>
                    axios.get(`${this.$store.state.apiUrl}/api/meetings`, { params: { team: g.name } }).then(resp => resp.data).catch(() => [])));
                this.meetings = [
                    ...mine.data.map((m: any) => ({ ...m, Group: null })),
                    ...byGroup.flatMap((list: any[], i: number) => list.map((m: any) => ({ ...m, Group: this.groups[i].name })))
                ];
            }
        } catch(e) {
            console.log(e);
        }

        // The meeting being run or looked at, else one of team's (the last one used if it was one), else the one
        // used last time, else the first
        let last: any = null;
        try {
            last = Number(localStorage.getItem('quickAddMeetingID'));
        } catch(e) { /* storage unavailable */ }
        const viewing = this.fixedMeeting ? this.fixedMeeting.ID : this.$route.path == '/meetings' ? Number(this.$route.query.meetingID) : null;
        const teamMeetings = this.team ? this.meetings.filter((m: any) => m.Team == this.team) : [];
        const teamPick = teamMeetings.some((m: any) => m.ID == last) ? last : teamMeetings[0]?.ID;
        const pick = [ viewing, teamPick, last ].find(id => id && this.meetings.some((m: any) => m.ID == id));
        this.meetingID = pick || this.meetings[0]?.ID || null;
        await this.meetingChange();
        this.loaded = true;
        this.$nextTick(() => (this.$refs.nameInput as HTMLInputElement)?.focus());
    },
    computed: {
        meeting(): any {
            return this.meetings.find((m: any) => m.ID == this.meetingID) || null;
        },
        // The red "no due date" note, until a date is picked
        dueDateNudge(): boolean {
            return this.dueDateAsked && !this.form.DueDate;
        },
        // The columns showing on its board
        columns(): any[] {
            return JSON.parse(this.meeting?.ColumnsJSON || '[]').filter((c: any) => c.show !== false && c.name);
        },
        meetingGroups(): any[] {
            const out = [];
            const mine = this.meetings.filter((m: any) => !m.Group);
            if(mine.length) {
                out.push({ label: 'My Meetings', meetings: mine });
            }
            for(const g of this.groups) {
                const list = this.meetings.filter((m: any) => m.Group == g.name);
                if(list.length) {
                    out.push({ label: g.name, meetings: list });
                }
            }
            return out;
        },
        // As on the meeting board: who can open it (team + Team Hub admins) for a team meeting, the creator
        // and attendees for a personal one
        assigneeOptions(): any[] {
            const m = this.meeting;
            if(!m) {
                return [];
            }
            const ids = m.Team
                ? (this.teamMembers[m.Team] || [])
                : [ m.CreatedByEmployeeID, ...JSON.parse(m.AttendeeEmployeeIDsJSON || '[]') ].map(Number);
            return this.employees.filter((e: any) => ids.includes(e.ID));
        },
        assigneeName(): string {
            return this.employees.find((e: any) => e.ID == this.form.AssigneeEmployeeID)?.Name || '';
        }
    },
    methods: {
        async meetingChange() {
            const team = this.meeting?.Team;
            if(team && !this.teamMembers[team]) {
                await axios.get(`${this.$store.state.apiUrl}/api/meetings/attendee-options`, { params: { team } })
                    .then(resp => {
                        this.teamMembers[team] = resp.data.map((id: any) => Number(id));
                    })
                    .catch(e => {
                        console.log(e);
                    })
            }
            // An assignee who can't open the newly picked meeting is cleared
            if(this.form.AssigneeEmployeeID && !this.assigneeOptions.some((e: any) => e.ID == this.form.AssigneeEmployeeID)) {
                this.form.AssigneeEmployeeID = null;
            }
            // A column the newly picked meeting doesn't have: the preferColumn one if it has one, else the first
            if(!this.columns.some((c: any) => c.name == this.column)) {
                const prefer = this.preferColumn.toLowerCase();
                this.column = ((prefer && this.columns.find((c: any) => c.name.toLowerCase().includes(prefer))) || this.columns[0])?.name || '';
            }
        },
        async submit() {
            if(this.saving || !this.meeting) {
                return;
            }
            if(!this.form.Name.trim()) {
                alert('Include a name');
                return;
            }
            // No due date: suggested once (the red note), then the next Submit goes through
            if(!this.form.DueDate && !this.dueDateAsked) {
                this.dueDateAsked = true;
                return;
            }
            this.saving = true;
            await axios.post(`${this.$store.state.apiUrl}/api/meetingitems`, {
                ...this.form,
                Name: this.form.Name.trim(),
                MeetingID: this.meeting.ID,
                Column: this.column
            })
                .then(resp => {
                    try {
                        localStorage.setItem('quickAddMeetingID', String(this.meeting.ID));
                    } catch(e) { /* not remembered, that's fine */ }
                    // In the running meeting it's already on the board (the meeting's live updates)
                    this.$store.state.quickAddToast = this.fixedMeeting ? { text: `Added to ${this.column}.` } : {
                        text: `Added to ${this.meeting.Name}.`,
                        link: { label: 'View Item', to: `/meetings?meetingID=${this.meeting.ID}` + (resp.data?.ID ? `&itemID=${resp.data.ID}` : '') }
                    };
                    this.$emit('added', resp.data?.ID);
                    this.$emit('close');
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error');
                })
            this.saving = false;
        }
    }
});
</script>

<style scoped>
.quick-add-row { display: flex; gap: 15px; }
.quick-add-row > div { flex: 1; min-width: 0; }
.quick-add-row select, .quick-add-row input { width: 100%; margin: 0; }
.quick-add-note { color: var(--font-mid-gray); margin: 0 0 10px; }
</style>
